// sections/HeroSection.tsx
import React, { useState } from "react";
import { Row, Col, Button, UncontrolledTooltip } from "reactstrap";
import { Link } from "react-router-dom";
import { BicepsFlexed, ChevronLeft, ChevronRight, Rocket } from 'lucide-react';
import heroImage from "./images/photo-1503387762-592deb58ef4e.avif";
import './HeroSection.css';

interface HeroSectionProps {
}

interface VideoItem {
  id: string;
  title: string;
  description: string;
  url: string;
  thumbnail?: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ }) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  // Video carousel data - you can easily add more videos here
  const videos: VideoItem[] = [
    {
      id: "1",
      title: "🏠 BidBro 💪",
      description: "Save weeks of time & 20% or more on your home improvement project!",
      url: "https://youtube.com/shorts/ToEwqoZqlQA"
    },
    {
      id: "2",
      title: "BidBro: 🏠 YOUR COMPLETE HOME PROJECT SOLUTION! 🏠",
      description: "BidBro doesn't just connect you with contractors - we help manage your ENTIRE project from start to finish! ",
      url: "https://youtube.com/shorts/SdmLIh0xHLo"
    }

    // Add more videos here as needed:
    // {
    //   id: "3",
    //   title: "Creating Your First Job",
    //   description: "Step-by-step guide to posting your first project",
    //   url: "https://youtube.com/shorts/ANOTHER_VIDEO_ID"
    // }
  ];

  const nextVideo = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % videos.length);
  };

  const prevVideo = () => {
    setCurrentVideoIndex((prev) => (prev - 1 + videos.length) % videos.length);
  };

  const currentVideo = videos[currentVideoIndex];

  // Extract YouTube video ID for embedding
  const getYouTubeVideoId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeVideoId(currentVideo.url);

  return (
    <section
      className="hero-section position-relative text-white d-flex align-items-center my-3 my-md-5 my-lg-5"
      style={{
        backgroundImage: `url(${heroImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="overlay position-absolute"></div>
      <div className="container position-relative">
        <div className="max-w-5xl">
          <h1 className="display-4 fw-bold mb-4 text-light">
            Save Time & Money Hiring Contractors
          </h1>
          <p className="fs-4 mb-5">
            Post your job, get multiple quotes up-front, skip the meetings, and save up to 20% or more!
          </p>

          {/* Video Carousel */}
          <div className="bg-white rounded p-4 shadow mb-4">
            {/* Video Navigation - only show if more than 1 video */}
            {videos.length > 1 && (
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  color="light"
                  size="sm"
                  onClick={prevVideo}
                  disabled={videos.length <= 1}
                >
                  <ChevronLeft size={16} className="me-1" /> Previous
                </Button>
                <span className="text-muted small">
                  Video {currentVideoIndex + 1} of {videos.length}
                </span>
                <Button
                  color="light"
                  size="sm"
                  onClick={nextVideo}
                  disabled={videos.length <= 1}
                >
                  Next <ChevronRight size={16} className="ms-1" />
                </Button>
              </div>
            )}

            {/* Embedded YouTube Video */}
            {videoId && (
              <div className="ratio ratio-16x9">
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                  title={currentVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="rounded"
                ></iframe>
              </div>
            )}
          </div>

         {/* Release 2.1.0 Highlight */}
         <div className="bg-gradient-primary rounded p-3 shadow-sm latest-release-margins">
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center">
                  <div className="bg-warning rounded-circle p-2 me-3" style={{ width: '40px', height: '40px' }}>
                    <BicepsFlexed className="text-dark" size={20} />
                  </div>
                  <div>
                    <h5 className="mb-1 text-white fw-bold" >BidBro Release 2.1.0 is here</h5>
                    <h6 className="mb-1 text-white fw-bold" >Launched <Rocket className="ms-1 me-1" />08/11/2025!</h6>
                    <div className="mb-0 text-white-75">
                      <ul className="mb-0 ps-3" style={{ fontSize: '0.9rem' }}>
                        <li>Expanded coverage to include construction projects for 46 major US cities including New York, Los Angeles, Chicago, Houston, Phoenix, Philadelphia, San Antonio, San Diego, San Francisco, and 37 more major metropolitan areas</li>
                        <li>Now covering 24 states coast-to-coast with over <strong>27,000 public construction projects</strong></li>
                        <li>Significantly expanded opportunities for contractors and businesses nationwide</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-center text-md-end mt-2 mt-md-0">
                <Link
                  to="/jobs"
                  className="px-1 px-sm-2 px-md-3 py-1 py-md-2 me-2 btn btn-warning rounded text-nowrap"
                  style={{ fontSize: '0.8rem' }}
                >
                  <BicepsFlexed size={16} className="me-1" />
                  <span className="d-none d-sm-inline">Browse Projects</span>
                  <span className="d-sm-none">Projects</span>
                </Link>
                <Link
                  id="btnReleases"
                  to="/releases"
                  className="px-1 px-sm-2 px-md-3 py-1 py-md-2 btn btn-secondary rounded text-nowrap"
                  style={{ fontSize: '0.8rem' }}
                >
                  <BicepsFlexed size={16} className="me-1" />
                  <span className="d-none d-sm-inline">View Release Notes</span>
                  <span className="d-sm-none">Release Notes</span>
                </Link>
              </Col>
            </Row>
          </div>



        </div>
      </div>
    </section>
  );
};

export default HeroSection;

// Add these styles to your CSS file:
/*
.bg-gradient-primary {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
}

.text-white-75 {
  color: rgba(255, 255, 255, 0.75) !important;
}

.video-thumbnail:hover {
  transform: scale(1.05);
  transition: transform 0.2s ease;
}

.video-thumbnail:hover .position-absolute {
  background-color: #0056b3 !important;
}
*/